import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Search, X } from "lucide-react";
import { db } from "../../firebase";
import {
  collection,
  query,
  orderBy,
  startAfter,
  limit as firestoreLimit,
  getDocs,
  QueryDocumentSnapshot,
  QueryConstraint,
} from "firebase/firestore";

interface SearchResultPayload {
  results: any[];
  total: number;
  isSearching: boolean;
  page: number;
}

interface SearchBarProps {
  collectionName: string;
  searchFields: string[];
  onResults: (payload: SearchResultPayload) => void;
  placeholder?: string;
  baseConstraint?: QueryConstraint[]; // ✅ Security scope only
  filters?: Record<string, string>; // ✅ Client-side filters
  onLoading?: (isLoading: boolean) => void;
}

type PageCacheEntry = {
  results: any[];
  lastDocsPerField: Map<string, QueryDocumentSnapshot | null>;
};

const SearchBar = forwardRef((props: SearchBarProps, ref) => {
  const {
    collectionName,
    searchFields,
    onResults,
    placeholder = "Search...",
    baseConstraint = [],
    filters = {},
    onLoading,
  } = props;

  const [inputValue, setInputValue] = useState<string>("");
  const debouncedValueRef = useRef<string>("");
  const pagesRef = useRef<Map<number, PageCacheEntry>>(new Map());
  const lastSearchTextRef = useRef<string>("");
  const isSearchingRef = useRef<boolean>(false);
  const allResultsBufferRef = useRef<any[]>([]);
  const fieldCursorsRef = useRef<Map<string, QueryDocumentSnapshot | null>>(
    new Map(),
  );
  const isFullyFetchedRef = useRef<boolean>(false);

  const itemsPerPage = 20;

  useImperativeHandle(ref, () => ({
    fetchPage: async (page: number) => {
      if (onLoading) onLoading(true);
      try {
        return await fetchSearchPage(page);
      } finally {
        if (onLoading) onLoading(false);
      }
    },
    clearCache: () => {
      pagesRef.current.clear();
      allResultsBufferRef.current = [];
      fieldCursorsRef.current.clear();
      isFullyFetchedRef.current = false;
      lastSearchTextRef.current = "";
      isSearchingRef.current = false;
    },
  }));

  // Debounce input
  useEffect(() => {
    const handler = setTimeout(() => {
      const term = inputValue.trim().toLowerCase();
      debouncedValueRef.current = term;

      if (!term) {
        pagesRef.current.clear();
        allResultsBufferRef.current = [];
        fieldCursorsRef.current.clear();
        isFullyFetchedRef.current = false;
        lastSearchTextRef.current = "";
        isSearchingRef.current = false;
        onResults({ results: [], total: 0, isSearching: false, page: 1 });
        return;
      }

      // Always update local ref and clear cache to ensure fresh search when inputs/constraints change
      lastSearchTextRef.current = term;
      pagesRef.current.clear();
      allResultsBufferRef.current = [];
      fieldCursorsRef.current.clear();
      isFullyFetchedRef.current = false;

      if (onLoading) onLoading(true);

      fetchSearchPage(1)
        .catch((err) => {
          console.error("SearchBar fetchSearchPage(1) error:", err);
          onResults({ results: [], total: 0, isSearching: false, page: 1 });
        })
        .finally(() => {
          if (onLoading) onLoading(false);
        });
    }, 400);

    return () => clearTimeout(handler);
  }, [
    inputValue,
    collectionName,
    JSON.stringify(searchFields),
    JSON.stringify(baseConstraint),
    JSON.stringify(filters),
  ]);

  // Fetch one field with looping to ensure enough matches
  const runFieldQuery = useCallback(
    async (
      field: string,
      term: string,
      cursor: QueryDocumentSnapshot | null,
      limitAmount: number,
    ) => {
      const coll = collection(db, collectionName);
      let collected: QueryDocumentSnapshot[] = [];
      let currentCursor = cursor;
      const maxLoops = 10; // Increased loops to scan deeper per batch (10 * 100 = 1000 docs per field per step)

      for (let i = 0; i < maxLoops && collected.length < limitAmount; i++) {
        const batchLimit = limitAmount * 5; // Fetch 100 at a time

        const commonConstraints = [...baseConstraint, orderBy(field)];

        const q = currentCursor
          ? query(
              coll,
              ...commonConstraints,
              startAfter(currentCursor),
              firestoreLimit(batchLimit),
            )
          : query(coll, ...commonConstraints, firestoreLimit(batchLimit));

        const snap = await getDocs(q);
        if (snap.empty) {
          // If empty, this field is exhausted
          return { docs: collected, lastDoc: null, exhausted: true };
        }

        const filtered = snap.docs.filter((d) =>
          (d.data()[field] || "").toString().toLowerCase().includes(term),
        );

        collected.push(...filtered);
        currentCursor = snap.docs[snap.docs.length - 1];

        // If we got fewer docs than requested from DB, we likely hit end of collection for this sort
        if (snap.docs.length < batchLimit) {
          return { docs: collected, lastDoc: currentCursor, exhausted: true };
        }
      }

      return {
        docs: collected,
        lastDoc: currentCursor,
        exhausted: false,
      };
    },
    [collectionName, JSON.stringify(baseConstraint)],
  );

  const fetchMoreIntoBuffer = useCallback(async () => {
    if (isFullyFetchedRef.current) return;

    const term = lastSearchTextRef.current;

    const resultsMap = new Map();
    let anyFieldActive = false;

    await Promise.all(
      searchFields.map(async (field) => {
        const cursor = fieldCursorsRef.current.get(field) || null;

        // Fetch a chunk
        const result = await runFieldQuery(field, term, cursor, itemsPerPage);

        if (result.docs.length > 0) {
          result.docs.forEach((d) => resultsMap.set(d.id, d));
        }

        // Update cursor
        if (!result.exhausted || result.lastDoc) {
          fieldCursorsRef.current.set(field, result.lastDoc);
          anyFieldActive = true;
        }
      }),
    );

    // Add unique results to buffer AND Apply Client-Side Filters
    const newDocs = Array.from(resultsMap.values());
    let addedCount = 0;

    // We must respect existing buffer to dedup
    const existingIds = new Set(
      allResultsBufferRef.current.map((i) => i.ID || i.id),
    );

    for (const d of newDocs) {
      if (existingIds.has(d.id)) continue;

      const data = d.data();
      let matches = true;

      // Check against provided filters
      for (const [key, value] of Object.entries(filters)) {
        if (!value) continue; // skip empty filters
        // Comparison is strict string equality as per simplistic requirement
        // Ensure data field exists and matches
        if (String(data[key] || "") !== value) {
          matches = false;
          break;
        }
      }

      if (matches) {
        allResultsBufferRef.current.push({ ID: d.id, ...data });
        addedCount++;
      }

      existingIds.add(d.id);
    }

    if (!anyFieldActive) {
      isFullyFetchedRef.current = true;
    }
  }, [runFieldQuery, searchFields, filters]);

  const fetchSearchPage = useCallback(
    async (page: number) => {
      const term = lastSearchTextRef.current;
      if (!term) {
        const empty: PageCacheEntry = {
          results: [],
          lastDocsPerField: new Map(),
        };
        pagesRef.current.set(page, empty);
        onResults({ results: [], total: 0, isSearching: false, page });
        return empty;
      }

      const targetCount = page * itemsPerPage;

      // Fill buffer until we have enough or exhausted
      let safetyCounter = 0;
      const MAX_SAFETY_LOOPS = 50; // Safety break to prevent infinite loops (scans ~50k docs max)

      while (
        allResultsBufferRef.current.length < targetCount + 1 &&
        !isFullyFetchedRef.current
      ) {
        if (safetyCounter++ > MAX_SAFETY_LOOPS) {
          console.warn("Search safety limit reached. Stopping search.");
          break;
        }

        const prevLen = allResultsBufferRef.current.length;
        await fetchMoreIntoBuffer();

        // Removed the check that stops if result count didn't increase.
        // We must continue scanning until we find results or exhaust the DB.
        // The safetyCounter ensures we don't freeze the browser forever.
      }

      const all = allResultsBufferRef.current;
      const start = (page - 1) * itemsPerPage;
      const end = start + itemsPerPage;
      const slice = all.slice(start, end);

      // Update Total
      // If fully fetched, total is all.length.
      // If not, total is at least all.length.
      const total = isFullyFetchedRef.current ? all.length : all.length;

      const pageEntry: PageCacheEntry = {
        results: slice,
        lastDocsPerField: new Map(fieldCursorsRef.current),
      };

      pagesRef.current.set(page, pageEntry);

      isSearchingRef.current = true;
      onResults({
        results: slice,
        total: total,
        isSearching: true,
        page,
      });

      return pageEntry;
    },
    [fetchMoreIntoBuffer, itemsPerPage, onResults],
  );

  return (
    <div className="flex items-center gap-2">
      <div className="relative w-full max-w-sm min-w-xs">
        <span className="absolute inset-y-0 left-2 flex items-center text-gray-500">
          <Search size={16} />
        </span>
        <input
          type="text"
          placeholder={placeholder}
          className="w-full pl-8 pr-8 py-1 border rounded-md text-sm"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
        />
        {inputValue && (
          <button
            onClick={() => setInputValue("")}
            className="absolute inset-y-0 right-2 flex items-center"
          >
            <X size={16} className="text-gray-500 hover:text-gray-800" />
          </button>
        )}
      </div>
    </div>
  );
});

export default SearchBar;
